import { PhoneNumberUtil } from 'google-libphonenumber';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Link, useNavigate } from 'react-router-dom';
import lineLogo from '../../assets/channels/line.svg';
import viberLogo from '../../assets/channels/viber.jpeg';
import TelegramLoginButton from '../../components/TelegramLoginButton';
import { SMS_ENABLED_DIAL_CODES } from '../../constants/otp.const';
import { LOGO_PATH } from '../../constants/logo';
import { useApp } from '../../context/usePortal';
import { ayeApi } from '../../core';
import { useMutation } from '../../hooks/useMutation';
import { useLoginReturnPath } from '../../hooks/useLoginReturnPath';
import { useReferralCode } from '../../hooks/useReferralCode';

const EMAIL_LOGIN_ENABLED =
  process.env.REACT_APP_FEATURE_EMAIL_LOGIN === 'true';
const TELEGRAM_LOGIN_ENABLED =
  process.env.REACT_APP_FEATURE_TELEGRAM_LOGIN === 'true';
const OTP_LENGTH = 6;
const HAS_ALTERNATIVE_SIGN_IN = EMAIL_LOGIN_ENABLED || TELEGRAM_LOGIN_ENABLED;
const TELEGRAM_LOG_PREFIX = '[LoginPage][Telegram]';

const phoneUtil = PhoneNumberUtil.getInstance();
type LoginOtpChannel = 'sms' | 'whatsapp' | 'line' | 'call' | 'viber';
const OTP_CHANNEL_OPTIONS: LoginOtpChannel[] = [
  'whatsapp',
  'sms',
  'line',
  'call',
  'viber'
];

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    console.log(error);
  }
};
function classNames(...classNamees: Array<string | false | null | undefined>) {
  return classNamees.filter(Boolean).join(' ');
}

const maskPhoneForOtpMessage = (
  phone: string,
  dialCode: string | null
): string => {
  const normalizedDialCode = (dialCode ?? '').replace('+', '');
  const digitsOnly = phone.replace(/\D/g, '');

  const localNumber = normalizedDialCode
    ? digitsOnly.replace(new RegExp(`^${normalizedDialCode}`), '')
    : digitsOnly;

  if (!localNumber) return '********';
  if (localNumber.length <= 2) return localNumber;

  const lastTwo = localNumber.slice(-2);
  return `${'*'.repeat(Math.max(localNumber.length - 2, 6))}${lastTwo}`;
};

export default function Login() {
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');

  // Phone login state
  const [phoneNumber, setPhoneNumber] = useState<string>('+60');
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [phoneCountryIso2, setPhoneCountryIso2] = useState<string>('my');
  const [phoneValidationMessage, setPhoneValidationMessage] =
    useState<string>('');

  // Email login state
  const [email, setEmail] = useState<string>('');
  const [emailValidationMessage, setEmailValidationMessage] =
    useState<string>('');

  // Shared OTP state
  const [otp, setOtp] = useState<{
    tag?: 'success' | 'failed';
    message?: string;
  }>({});
  const [otpValue, setOtpValue] = useState<string>('');
  const [otpDigits, setOtpDigits] = useState<string[]>(
    new Array(OTP_LENGTH).fill('')
  );
  const [countdown, setCountdown] = useState<number>(60);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [otpButtonDisabled, setOtpButtonDisabled] = useState<boolean>(false);
  const [isLoginDisabled, setIsLoginDisabled] = useState<boolean>(false);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);

  const [showReferralToast, setShowReferralToast] = useState<boolean>(false);
  const [otpChannel, setOtpChannel] = useState<'sms' | 'whatsapp'>('whatsapp');
  const [isChannelDropdownOpen, setIsChannelDropdownOpen] =
    useState<boolean>(false);
  const {
    getCode,
    login,
    getCodeByEmail,
    loginWithEmail,
    getCodeByWhatsApp,
    loginWithTelegram
  } = useApp();
  const { refCode } = useReferralCode();

  const [telegramError, setTelegramError] = useState<string>('');
  const [telegramLoading, setTelegramLoading] = useState<boolean>(false);
  const normalizedCountryCode = (countryCode ?? '').replace('+', '');
  const maskedPhoneForOtp = maskPhoneForOtpMessage(phoneNumber, countryCode);
  const isSmsDisabledForCountry =
    loginMethod === 'phone' &&
    Boolean(normalizedCountryCode) &&
    !SMS_ENABLED_DIAL_CODES.has(normalizedCountryCode);

  const getChannelLabel = (channel: LoginOtpChannel) => {
    switch (channel) {
      case 'sms':
        return 'SMS';
      case 'whatsapp':
        return 'WhatsApp';
      case 'line':
        return 'Line';
      case 'call':
        return 'Call';
      case 'viber':
        return 'Viber';
      default:
        return channel;
    }
  };

  const renderChannelIcon = (channel: LoginOtpChannel) => {
    if (channel === 'line') {
      return <img src={lineLogo} alt="" className="h-4 w-4" />;
    }
    if (channel === 'viber') {
      return (
        <img
          src={viberLogo}
          alt=""
          className="h-4 w-4 rounded-sm object-cover"
        />
      );
    }
    if (channel === 'call') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C9.61 21 3 14.39 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" />
        </svg>
      );
    }
    if (channel === 'sms') {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
          />
        </svg>
      );
    }

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="#25D366"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  };

  useEffect(() => {
    if (refCode) {
      setShowReferralToast(true);
    } else {
      setShowReferralToast(false);
    }
  }, [refCode]);

  // Non-blocking geo prefill for country code on login page.
  useEffect(() => {
    let mounted = true;

    const prefillFromGeo = async () => {
      try {
        const response = await ayeApi.getRegistrationCountryPrefill({});
        if (!mounted || !response.success) return;

        const hasExternalPrefill =
          sessionStorage.getItem('external_prefill_phone_number') ||
          sessionStorage.getItem('external_prefill_country_code');

        if (hasExternalPrefill) return;

        const geoCountry = (response.data?.countryCode ?? 'MY').toLowerCase();
        const geoDialCode = (response.data?.dialCode ?? '+60').replace('+', '');

        setPhoneCountryIso2(geoCountry.length === 2 ? geoCountry : 'my');
        setCountryCode(geoDialCode);
        setPhoneNumber(`+${geoDialCode}`);
      } catch {
        // Non-blocking by design: ignore geo prefill failures.
      }
    };

    void prefillFromGeo();

    return () => {
      mounted = false;
    };
  }, []);

  // Registration rule parity: SMS is only enabled for MY (+60).
  useEffect(() => {
    if (!isSmsDisabledForCountry) return;
    if (otpChannel !== 'sms') return;
    setOtpChannel('whatsapp');
  }, [isSmsDisabledForCountry, otpChannel]);

  useMutation(ayeApi.loginPortal, {
    onSuccess: () => {
      console.log('Success');
    }
  });
  const navigate = useNavigate();
  const afterLogin = useLoginReturnPath();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isTimerActive && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setIsTimerActive(false);
    }
    return () => clearTimeout(timer);
  }, [isTimerActive, countdown]);

  const pendingWebOtpVerify = useRef(false);

  // WebOTP API: auto-fill OTP from SMS on supported browsers (SMS channel only)
  useEffect(() => {
    if (!isOtpSent || otpChannel !== 'sms') return;
    if (!('OTPCredential' in window)) return;
    const ac = new AbortController();
    (
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal
      } as CredentialRequestOptions) as Promise<{ code?: string }>
    )
      .then((credential) => {
        if (!credential?.code) return;
        const code = String(credential.code)
          .replace(/\D/g, '')
          .slice(0, OTP_LENGTH);
        const digits = code
          .split('')
          .concat(new Array(OTP_LENGTH).fill(''))
          .slice(0, OTP_LENGTH);
        setOtpDigits(digits);
        setOtpValue(code);
        pendingWebOtpVerify.current = true;
      })
      .catch(() => {
        /* AbortError or unsupported — ignore */
      });
    return () => ac.abort();
  }, [isOtpSent, otpChannel]);

  // Auto-verify once WebOTP has updated otpValue in state
  useEffect(() => {
    if (!pendingWebOtpVerify.current) return;
    if (otpValue.length !== OTP_LENGTH) return;
    pendingWebOtpVerify.current = false;
    void verifyOtp();
  }, [otpValue]);

  const resetOtpFlow = () => {
    setOtp({});
    setOtpValue('');
    setOtpDigits(new Array(OTP_LENGTH).fill(''));
    setIsOtpSent(false);
    setIsTimerActive(false);
    setCountdown(60);
    setIsLoginDisabled(false);
  };

  const handlePhoneNumberChange = (value: string, data: CountryData) => {
    const previousPhoneNumber = phoneNumber;
    const formattedValue = value.startsWith('+') ? value : `+${value}`;

    // Normalize dial code as string
    const dialCode = String(data?.dialCode || '60');

    setPhoneNumber(formattedValue);
    setCountryCode(dialCode);

    if (data?.countryCode) {
      setPhoneCountryIso2(data.countryCode.toLowerCase());
    }

    setIsChannelDropdownOpen(false);
    if (previousPhoneNumber !== formattedValue) {
      resetOtpFlow();
    }

    if (formattedValue) {
      if (isPhoneValid(formattedValue)) {
        setPhoneValidationMessage('');
      } else {
        setPhoneValidationMessage('Please enter a valid phone number.');
      }
    } else {
      setPhoneValidationMessage('Phone number is required.');
    }
  };

  const handleOtpDigitChange = (
    event: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const next = [...otpDigits];
    next[index] = event.target.value.replace(/[^0-9]/g, '').slice(-1);
    setOtpDigits(next);
    setOtpValue(next.join(''));

    if (next[index] && index < OTP_LENGTH - 1) {
      document.getElementById(`login-otp-${index + 1}`)?.focus();
    }

    if (otp.message) {
      setOtp({});
    }
  };

  const handleOtpDigitKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key !== 'Backspace') return;

    if (otpDigits[index]) {
      const next = [...otpDigits];
      next[index] = '';
      setOtpDigits(next);
      setOtpValue(next.join(''));
      return;
    }

    if (index > 0) {
      document.getElementById(`login-otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, OTP_LENGTH);

    if (!pasted) return;

    const next = new Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }

    setOtpDigits(next);
    setOtpValue(next.join(''));
    document
      .getElementById(`login-otp-${Math.min(pasted.length, OTP_LENGTH - 1)}`)
      ?.focus();

    if (otp.message) {
      setOtp({});
    }
  };

  const handleGetCode = async (channel?: 'sms' | 'whatsapp') => {
    setOtpButtonDisabled(true);
    setOtpValue('');

    if (loginMethod === 'email') {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailValidationMessage('Please enter a valid email address.');
        setOtpButtonDisabled(false);
        return;
      }
      setEmailValidationMessage('');
      setOtp({});

      const { response } = await getCodeByEmail(email, 'login');
      setIsLoginDisabled(false);

      if (response?.success) {
        setIsOtpSent(true);
        setOtpDigits(new Array(OTP_LENGTH).fill(''));
        setOtpValue('');
        setOtp({ tag: 'success', message: response?.data?.message });
      } else {
        setIsOtpSent(false);
        setOtp({ tag: 'failed', message: response?.error?.message });
      }

      setOtpButtonDisabled(false);
      if (response?.success) {
        setIsTimerActive(true);
        setCountdown(60);
      }
      return;
    }

    // Phone mode
    if (!phoneNumber || !isPhoneValid(phoneNumber)) {
      const isValidInput = !isPhoneValid(phoneNumber);
      console.log(isValidInput);
      if (!isValidInput) {
        setPhoneValidationMessage('Please enter only digits.');
        setOtpButtonDisabled(false);
        return;
      }
      setPhoneValidationMessage('Phone number must have correct digits.');
      setOtpButtonDisabled(false);
      return;
    }
    setPhoneValidationMessage('');
    setOtp({});

    const phone_string =
      countryCode + '-' + phoneNumber.replace(`+${countryCode}`, '');
    const selectedChannel = channel ?? otpChannel;
    setOtpChannel(selectedChannel);
    const { response } =
      selectedChannel === 'whatsapp'
        ? await getCodeByWhatsApp(phone_string, 'login')
        : await getCode(phone_string, 'login');
    setIsLoginDisabled(false);

    if (response?.success) {
      setIsOtpSent(true);
      setOtpDigits(new Array(OTP_LENGTH).fill(''));
      setOtpValue('');
      setOtp({
        tag: 'success',
        message: response?.data?.message
      });
    } else {
      setIsOtpSent(false);
      setOtp({
        tag: 'failed',
        message: response?.error?.message
      });
    }

    setOtpButtonDisabled(false);
    if (response?.success) {
      setIsTimerActive(true);
      setCountdown(60);
    }
  };

  const verifyOtp = async () => {
    if (!otpValue) {
      setOtp({ tag: 'failed', message: 'Please fill OTP in field above' });
      return;
    } else {
      setOtp({});
    }

    if (loginMethod === 'email') {
      const res = await loginWithEmail(email, otpValue);
      if (res.response?.success) {
        setOtpValue('');
        return navigate(afterLogin);
      } else {
        setOtp({
          tag: 'failed',
          message: res.response?.error?.message
        });
        if (res.response?.error?.code === 'OTP_FAILED_ATTEMPTS') {
          setIsLoginDisabled(true);
          setIsTimerActive(false);
          setCountdown(0);
        }
      }
      return;
    }

    const res = await login(
      countryCode + '-' + phoneNumber.replace(`+${countryCode}`, ''),
      otpValue
    );

    if (res.response?.success) {
      setOtpValue('');
      return navigate(afterLogin);
    } else {
      setOtp({
        tag: 'failed',
        message: res.response?.error?.message
      });
      if (res.response?.error?.code === 'OTP_FAILED_ATTEMPTS') {
        // Keeps entering incorrect OTP
        setIsLoginDisabled(true);
        setIsTimerActive(false);
        setCountdown(0);
      }
    }
  };

  const handleTelegramResult = async (
    result:
      | { success: true; idToken: string }
      | { success: false; error: 'cancelled' | 'phone_denied' | 'error' }
  ) => {
    console.info(TELEGRAM_LOG_PREFIX, 'Received Telegram result', {
      success: result.success,
      error: result.success ? undefined : result.error
    });
    setTelegramError('');

    if (!result.success) {
      if (result.error === 'cancelled') return;
      if (result.error === 'phone_denied') {
        setTelegramError(
          'Phone access is required. Please allow Telegram to share your phone number.'
        );
      } else {
        setTelegramError('Telegram login failed. Please try again.');
      }
      return;
    }

    setTelegramLoading(true);
    try {
      console.info(
        TELEGRAM_LOG_PREFIX,
        'Calling loginWithTelegram backend endpoint'
      );
      const res = await loginWithTelegram(result.idToken);
      if (res.response?.success) {
        console.info(TELEGRAM_LOG_PREFIX, 'Login success, redirecting');
        navigate(afterLogin);
      } else {
        const code = res.response?.error?.code;
        console.warn(TELEGRAM_LOG_PREFIX, 'Login failed response', {
          code,
          message: res.response?.error?.message
        });
        if (code === 'USER_NOT_REGISTERED') {
          setTelegramError(
            'Your Telegram phone number is not registered. Please register first.'
          );
        } else if (code === 'TELEGRAM_PHONE_NOT_SHARED') {
          setTelegramError(
            'Phone access is required. Please allow Telegram to share your phone number.'
          );
        } else {
          setTelegramError(
            res.response?.error?.message ||
              'Telegram login failed. Please try again.'
          );
        }
      }
    } finally {
      console.info(TELEGRAM_LOG_PREFIX, 'Telegram login flow finished');
      setTelegramLoading(false);
    }
  };

  useEffect(() => {
    const externalSessionId = sessionStorage.getItem('external_session_id');
    const externalCustId = sessionStorage.getItem('external_cust_id');
    const externalPhoneNumber = sessionStorage.getItem(
      'external_prefill_phone_number'
    );
    const externalCountryCode = sessionStorage.getItem(
      'external_prefill_country_code'
    );
    const externalFullName = sessionStorage.getItem(
      'external_prefill_full_name'
    );
    const externalEmail = sessionStorage.getItem('external_prefill_email');
    const externalSessionExpiresAt = sessionStorage.getItem(
      'external_session_expires_at'
    );

    // later implement some better solution

    sessionStorage.clear();

    if (externalSessionId) {
      sessionStorage.setItem('external_session_id', externalSessionId);
    }

    if (externalCustId) {
      sessionStorage.setItem('external_cust_id', externalCustId);
    }

    if (externalPhoneNumber) {
      sessionStorage.setItem(
        'external_prefill_phone_number',
        externalPhoneNumber
      );
    }

    if (externalCountryCode) {
      sessionStorage.setItem(
        'external_prefill_country_code',
        externalCountryCode
      );
    }

    if (externalFullName) {
      sessionStorage.setItem('external_prefill_full_name', externalFullName);
    }

    if (externalEmail) {
      sessionStorage.setItem('external_prefill_email', externalEmail);
    }

    if (externalSessionExpiresAt) {
      sessionStorage.setItem(
        'external_session_expires_at',
        externalSessionExpiresAt
      );
    }
  }, []);

  useEffect(() => {
    const externalPhoneNumber = sessionStorage.getItem(
      'external_prefill_phone_number'
    );
    const externalCountryCode = sessionStorage.getItem(
      'external_prefill_country_code'
    );

    if (!externalPhoneNumber || !externalCountryCode) {
      return;
    }

    const normalizedCountryCode = externalCountryCode.replace('+', '');
    const formattedPhoneNumber = `+${normalizedCountryCode}${externalPhoneNumber}`;

    setCountryCode(normalizedCountryCode);
    setPhoneNumber(formattedPhoneNumber);

    if (isPhoneValid(formattedPhoneNumber)) {
      setPhoneValidationMessage('');
    }
  }, []);

  return (
    <>
      {showReferralToast && (
        <div className="fixed left-1/2 top-4 z-50 flex w-[95vw] max-w-md -translate-x-1/2 items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-md">
          <div className="text-sm font-medium text-gray-700">
            You have been referred!
            <br />
            <span className="text-xs text-gray-500">
              Log in/Register Now to claim your reward
            </span>
          </div>
          <button
            className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Dismiss referral message"
            onClick={() => setShowReferralToast(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}
      <div className="flex w-screen items-center justify-center bg-gray-50 text-gray-600">
        <div className="relative">
          <div className="relative mt-24 flex flex-col rounded-lg border-gray-400 bg-white px-4 shadow-sm sm:w-[30rem]">
            <div className="flex-auto p-6">
              <div className="mb-3 flex flex-shrink-0 flex-grow-0 items-center justify-center overflow-hidden">
                <img
                  className="mb-3 h-12 w-auto"
                  src={LOGO_PATH}
                  alt="Logo"
                />{' '}
              </div>
              <h4 className="mb-2 text-center font-medium text-gray-700 xl:text-2xl">
                Welcome to{' '}
                <span className="inline-block bg-gradient-to-r from-orange-600 to-orange-300 bg-clip-text font-medium text-transparent xl:text-2xl">
                  AyeFace
                </span>{' '}
                User Portal
              </h4>
              <p className="mb-6 text-center text-gray-500">
                Please sign-in using OTP to access your account
              </p>

              <form id="" className="mb-4" action="#" method="POST">
                <div className="mb-4">
                  {loginMethod === 'phone' ? (
                    <>
                      <span className="mb-2 block text-xs font-medium uppercase text-gray-700">
                        Phone Number
                      </span>
                      <PhoneInput
                        country={phoneCountryIso2}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        inputStyle={{ width: '100%' }}
                        enableSearch
                        disableCountryCode={false}
                        countryCodeEditable={true}
                        placeholder="Enter your phone number"
                        autoFormat={false}
                      />
                      {phoneValidationMessage && (
                        <div className="text-sm text-red-600">
                          {phoneValidationMessage}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-xs font-medium uppercase text-gray-700"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => {
                          if (email !== e.target.value) {
                            resetOtpFlow();
                          }
                          setEmail(e.target.value);
                          if (emailValidationMessage) {
                            setEmailValidationMessage('');
                          }
                        }}
                        placeholder="Enter your email address"
                        className="focus:shadow-outline block w-full cursor-text appearance-none rounded-md border border-gray-400 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-500 focus:outline-none"
                        autoFocus
                      />
                      {emailValidationMessage && (
                        <div className="text-sm text-red-600">
                          {emailValidationMessage}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="mb-4">
                  {loginMethod === 'phone' ? (
                    <>
                      {isOtpSent && (
                        <>
                          <div className="rounded-lg border border-green-100 bg-green-50 px-3 py-2">
                            <p className="text-xs font-medium text-green-700">
                              A OTP (One Time Passcode) has been sent to{' '}
                              {maskedPhoneForOtp} via{' '}
                              {getChannelLabel(otpChannel)}. Please enter the
                              OTP in the field below to verify your phone.
                            </p>
                          </div>

                          <div className="mt-2">
                            <label className="mb-1 block text-xs font-medium uppercase text-gray-700">
                              Enter OTP
                            </label>
                            <div className="grid grid-cols-6 gap-2">
                              {otpDigits.map((digit, i) => (
                                <input
                                  key={`login-otp-${i}`}
                                  id={`login-otp-${i}`}
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="one-time-code"
                                  maxLength={1}
                                  value={digit}
                                  onChange={(event) =>
                                    handleOtpDigitChange(event, i)
                                  }
                                  onKeyDown={(event) =>
                                    handleOtpDigitKeyDown(event, i)
                                  }
                                  onPaste={handleOtpPaste}
                                  className="h-10 rounded-lg border border-gray-300 text-center text-base font-semibold text-gray-800 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                                  disabled={isLoginDisabled}
                                />
                              ))}
                            </div>
                          </div>

                          <div className="mt-3 space-y-3">
                            <button
                              type="button"
                              onClick={verifyOtp}
                              className={`min-h-12 w-full rounded-md px-8 py-2 text-sm font-semibold ${
                                isLoginDisabled ||
                                otpValue.length !== OTP_LENGTH
                                  ? 'cursor-not-allowed border border-gray-300 bg-gray-100 text-gray-400'
                                  : 'bg-orange-500 text-white hover:bg-orange-600'
                              }`}
                              disabled={
                                isLoginDisabled ||
                                otpValue.length !== OTP_LENGTH
                              }
                            >
                              Verify
                            </button>

                            <div className="rounded-md border border-gray-200 bg-gray-50 p-2.5">
                              <p className="text-xs font-medium text-gray-600">
                                Didn&apos;t receive the code?
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {isTimerActive ? (
                                  <>
                                    Request a new OTP in{' '}
                                    <span className="font-semibold text-orange-600">
                                      {countdown}s
                                    </span>
                                  </>
                                ) : (
                                  'Choose a channel below to resend OTP'
                                )}
                              </p>

                              <div className="mt-2 grid grid-cols-2 gap-2">
                                {(
                                  ['whatsapp', 'sms'] as Array<
                                    'sms' | 'whatsapp'
                                  >
                                ).map((ch) => {
                                  const isDisabled =
                                    isTimerActive ||
                                    otpButtonDisabled ||
                                    (ch === 'sms' && isSmsDisabledForCountry);
                                  const isActive = otpChannel === ch;

                                  return (
                                    <button
                                      key={ch}
                                      type="button"
                                      disabled={isDisabled}
                                      className={`flex min-h-12 w-full items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold ${
                                        isDisabled
                                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                                          : isActive
                                            ? 'border-orange-300 bg-orange-50 text-orange-700'
                                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                      }`}
                                      onClick={() => {
                                        setOtpChannel(ch as 'sms' | 'whatsapp');
                                        void handleGetCode(
                                          ch as 'sms' | 'whatsapp'
                                        );
                                      }}
                                    >
                                      {renderChannelIcon(ch)}
                                      <span>{getChannelLabel(ch)}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                      {!isOtpSent && (
                        <>
                          <div className="mb-2 mt-3 text-xs font-medium uppercase text-gray-700">
                            Get OTP via
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              className="flex h-11 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700"
                              onClick={() =>
                                setIsChannelDropdownOpen((prev) => !prev)
                              }
                              disabled={otpButtonDisabled}
                            >
                              <span className="flex items-center gap-2">
                                {renderChannelIcon(otpChannel)}
                                {getChannelLabel(otpChannel)}
                              </span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 text-gray-500 transition-transform ${
                                  isChannelDropdownOpen ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>

                            {isChannelDropdownOpen && (
                              <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white p-1 shadow-lg">
                                {OTP_CHANNEL_OPTIONS.map((channel) => {
                                  const isDisabled =
                                    channel === 'sms'
                                      ? isSmsDisabledForCountry
                                      : channel !== 'whatsapp';

                                  return (
                                    <button
                                      key={channel}
                                      type="button"
                                      disabled={isDisabled}
                                      className={`mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm last:mb-0 ${
                                        isDisabled
                                          ? 'cursor-not-allowed text-gray-400'
                                          : 'text-gray-700 hover:bg-gray-50'
                                      } ${
                                        otpChannel === channel
                                          ? 'bg-green-50 font-medium text-green-700'
                                          : ''
                                      }`}
                                      onClick={() => {
                                        if (
                                          channel === 'sms' ||
                                          channel === 'whatsapp'
                                        ) {
                                          setOtpChannel(channel);
                                          setIsChannelDropdownOpen(false);
                                        }
                                      }}
                                    >
                                      {renderChannelIcon(channel)}
                                      <span>{getChannelLabel(channel)}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleGetCode(otpChannel)}
                            className={`mt-3 flex h-11 w-full items-center justify-center rounded-md px-3 text-sm font-medium ${
                              isTimerActive ||
                              (otpChannel === 'sms' && isSmsDisabledForCountry)
                                ? 'cursor-not-allowed border border-gray-400 bg-gray-100 text-gray-900 hover:bg-gray-200'
                                : 'bg-orange-500 text-white hover:bg-orange-600'
                            }`}
                            disabled={
                              isTimerActive ||
                              otpButtonDisabled ||
                              (otpChannel === 'sms' && isSmsDisabledForCountry)
                            }
                          >
                            {otpButtonDisabled && !isTimerActive ? (
                              <div className="flex items-center">
                                <svg
                                  className="mr-2 h-4 w-4 animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Sending...
                              </div>
                            ) : isTimerActive ? (
                              `${countdown} seconds`
                            ) : (
                              `Get OTP via ${getChannelLabel(otpChannel)}`
                            )}
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {isOtpSent && (
                        <>
                          <label
                            htmlFor="otp"
                            className="mb-2 block text-xs font-medium uppercase text-gray-700"
                          >
                            OTP Code
                          </label>
                          <div className="grid grid-cols-6 gap-2">
                            {otpDigits.map((digit, i) => (
                              <input
                                key={`login-email-otp-${i}`}
                                id={`login-otp-${i}`}
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                autoComplete="one-time-code"
                                maxLength={1}
                                value={digit}
                                onChange={(event) =>
                                  handleOtpDigitChange(event, i)
                                }
                                onKeyDown={(event) =>
                                  handleOtpDigitKeyDown(event, i)
                                }
                                onPaste={handleOtpPaste}
                                className="h-10 rounded-lg border border-gray-300 text-center text-base font-semibold text-gray-800 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-100"
                                autoFocus={i === 0}
                              />
                            ))}
                          </div>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleGetCode()}
                        className={`mt-3 flex h-11 w-full items-center justify-center rounded-md px-3 text-sm font-medium ${
                          isTimerActive
                            ? 'cursor-not-allowed border border-gray-400 bg-gray-100 text-gray-900 hover:bg-gray-200'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                        disabled={isTimerActive || otpButtonDisabled}
                      >
                        {otpButtonDisabled && !isTimerActive ? (
                          <div className="flex items-center">
                            <svg
                              className="mr-2 h-4 w-4 animate-spin"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Get OTP
                          </div>
                        ) : isTimerActive ? (
                          `${countdown} seconds`
                        ) : (
                          'Get OTP'
                        )}
                      </button>
                    </>
                  )}
                  {otp.message && otp.tag === 'failed' ? (
                    <div className={classNames('text-red-500', 'mt-3 text-sm')}>
                      {otp.message}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>

                {isOtpSent && loginMethod === 'email' && (
                  <div className="mb-3 mt-5">
                    <button
                      type="button"
                      onClick={verifyOtp}
                      className={`grid w-full cursor-pointer select-none rounded-md border px-5 py-2 text-center align-middle text-sm font-bold shadow focus:shadow-none ${!isLoginDisabled ? 'border-orange-500 bg-orange-500 text-white hover:border-orange-600 hover:bg-orange-600 hover:text-white focus:border-orange-600 focus:bg-orange-600 focus:text-white' : 'cursor-not-allowed border-gray-400 bg-gray-100 text-gray-900 hover:bg-gray-200'}`}
                      disabled={isLoginDisabled}
                    >
                      Sign in
                    </button>
                  </div>
                )}

                {HAS_ALTERNATIVE_SIGN_IN && (
                  <>
                    <div className="relative my-4 flex items-center">
                      <div className="flex-grow border-t border-gray-200" />
                      <span className="mx-3 flex-shrink text-xs text-gray-400">
                        OR
                      </span>
                      <div className="flex-grow border-t border-gray-200" />
                    </div>
                    <div className="mb-5 space-y-3">
                      <p className="text-center text-xs font-medium uppercase tracking-wide text-gray-400">
                        Other sign-in options
                      </p>
                      {EMAIL_LOGIN_ENABLED && (
                        <button
                          type="button"
                          onClick={() => {
                            setLoginMethod((prev) =>
                              prev === 'phone' ? 'email' : 'phone'
                            );
                            setOtpChannel('sms');
                            setTelegramError('');
                            resetOtpFlow();
                          }}
                          className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-5 py-2 text-center text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                        >
                          {loginMethod === 'phone' ? (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                                />
                              </svg>
                              Login via Email
                            </>
                          ) : (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3"
                                />
                              </svg>
                              Use Phone Number Instead
                            </>
                          )}
                        </button>
                      )}
                      {TELEGRAM_LOGIN_ENABLED && (
                        <>
                          {telegramLoading ? (
                            <div className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-500 shadow-sm">
                              <svg
                                className="h-4 w-4 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Signing in with Telegram...
                            </div>
                          ) : (
                            <TelegramLoginButton
                              onResult={handleTelegramResult}
                              disabled={telegramLoading}
                            />
                          )}
                          <p className="text-center text-xs text-gray-400">
                            Use Telegram to continue with your verified phone
                            number without OTP.
                          </p>
                          {telegramError && (
                            <div className="text-sm text-red-500">
                              {telegramError}
                              {telegramError.includes('not registered') && (
                                <>
                                  {' '}
                                  <Link
                                    to="/register"
                                    className="font-medium text-orange-500 hover:text-orange-600"
                                  >
                                    Register now
                                  </Link>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}

                <p className="mb-3 text-center">
                  Don't have an account?
                  <Link
                    to="/register"
                    className="cursor-pointer text-orange-500 no-underline hover:text-orange-600"
                  >
                    {' '}
                    Register Now{' '}
                  </Link>
                  <br></br>
                  <span className="mt-2 text-sm">
                    Use your phone for an optimal registration experience
                  </span>
                </p>
              </form>
            </div>
            <div className="mb-4 text-center text-sm">
              Need help?{' '}
              <button
                onClick={() =>
                  window.open(
                    'https://api.whatsapp.com/send?phone=60162666192',
                    '_blank'
                  )
                }
                className="cursor-pointer text-orange-500 no-underline hover:text-orange-600"
              >
                Get in touch!
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 pb-5 pt-10 text-center text-xs text-gray-500 sm:text-sm">
        <div className="mb-2 flex items-center justify-center gap-2">
          {/* <span className="font-semibold text-gray-600">Payment via</span>
          <img
            src={FIUU_LOGO_PATH}
            alt="Fiuu"
            className="h-6 rounded bg-[#0047BA] py-1 pl-2"
          /> */}
          <span className="text-xs font-semibold text-gray-600">
            Proudly Made in Southeast Asia ♥️
          </span>
        </div>
        © {new Date().getFullYear()} Aye Solutions. All rights reserved.
      </div>
    </>
  );
}
